import type { HiringDecision, InterviewStatus, JobStatus, Recommendation } from '@ai-interview/shared';
import { ApiError } from '../lib/api';

const STATUS_LABELS: Record<InterviewStatus, string> = {
  invited: 'Invited',
  in_progress: 'In progress',
  completed: 'Awaiting score',
  scored: 'Scored',
  scoring_failed: 'Scoring failed',
  expired: 'Expired',
  abandoned: 'Abandoned',
  revoked: 'Revoked',
};

const STATUS_TONE: Record<InterviewStatus, string> = {
  invited: '',
  in_progress: 'caution',
  completed: 'caution',
  scored: 'positive',
  scoring_failed: 'negative',
  expired: 'negative',
  abandoned: 'negative',
  revoked: '',
};

export function StatusBadge({ status }: { status: InterviewStatus }) {
  return <span className={`badge ${STATUS_TONE[status]}`}>{STATUS_LABELS[status]}</span>;
}

const JOB_STATUS_LABELS: Record<JobStatus, string> = { draft: 'Draft', open: 'Open', closed: 'Closed' };
const JOB_STATUS_TONE: Record<JobStatus, string> = { draft: 'caution', open: 'positive', closed: '' };

export function JobStatusBadge({ status }: { status: JobStatus }) {
  return <span className={`badge ${JOB_STATUS_TONE[status]}`}>{JOB_STATUS_LABELS[status]}</span>;
}

export const DECISION_LABELS: Record<HiringDecision, string> = {
  advance: 'Advance',
  hold: 'On hold',
  reject: 'Rejected',
};

const DECISION_TONE: Record<HiringDecision, string> = {
  advance: 'positive',
  hold: 'caution',
  reject: 'negative',
};

export function DecisionBadge({ value }: { value: HiringDecision }) {
  return <span className={`badge ${DECISION_TONE[value]}`}>{DECISION_LABELS[value]}</span>;
}

const RECOMMENDATION_LABELS: Record<Recommendation, string> = {
  strong_yes: 'Strong yes',
  yes: 'Yes',
  maybe: 'Maybe',
  no: 'No',
};

const RECOMMENDATION_TONE: Record<Recommendation, string> = {
  strong_yes: 'positive',
  yes: 'positive',
  maybe: 'caution',
  no: 'negative',
};

export function RecommendationBadge({ value }: { value: Recommendation }) {
  return <span className={`badge ${RECOMMENDATION_TONE[value]}`}>{RECOMMENDATION_LABELS[value]}</span>;
}

export function ErrorNotice({ error }: { error: unknown }) {
  if (!error) return null;
  const problems = error instanceof ApiError ? describeProblems(error.details) : [];
  return (
    <div className="notice error" role="alert">
      {problems.length > 0
        ? 'Some details need fixing:'
        : error instanceof Error
          ? error.message
          : String(error)}
      {problems.length > 0 ? (
        <ul className="small" style={{ margin: '0.4rem 0 0', paddingLeft: '1.1rem' }}>
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/** What the forms call each field the API validates. */
const FIELD_NAMES: Record<string, string> = {
  title: 'Title',
  department: 'Department',
  location: 'Location',
  seniority: 'Seniority',
  description: 'Job description',
  competencies: 'Competencies',
  questions: 'Questions',
  durationMinutes: 'Minutes',
  language: 'Language',
  status: 'Status',
  'candidate.name': 'Name',
  'candidate.email': 'Email',
  'candidate.phone': 'Phone',
  'candidate.background': 'Candidate brief',
  expiresInHours: 'Link valid (hours)',
  text: 'Resume text',
  data: 'Resume file',
};

/**
 * Turns the API's per-field validation errors - keyed by paths such as
 * `questions.2.text` - into sentences named the way the form labels things.
 */
function describeProblems(details: unknown): string[] {
  if (!details || typeof details !== 'object') return [];
  return Object.entries(details as Record<string, unknown>).flatMap(([path, messages]) =>
    (Array.isArray(messages) ? messages : [messages]).map((message) => {
      const text = humanise(String(message));
      return path === '_' ? text : `${fieldName(path)} ${text}`;
    }),
  );
}

function fieldName(path: string): string {
  const question = /^questions\.(\d+)(?:\.(\w+))?$/.exec(path);
  if (question) {
    const label = `Question ${Number(question[1]) + 1}`;
    return !question[2] || question[2] === 'text' ? label : `${label} ${question[2]}`;
  }
  const competency = /^competencies\.(\d+)$/.exec(path);
  if (competency) return `Competency ${Number(competency[1]) + 1}`;
  return FIELD_NAMES[path] ?? path;
}

/** Zod's messages, rephrased to follow a field name. */
function humanise(message: string): string {
  const rules: [RegExp, string][] = [
    [/^Too small: expected string to have >=?(\d+) characters?$/, 'must be at least $1 characters.'],
    [/^Too big: expected string to have <=?(\d+) characters?$/, 'must be at most $1 characters.'],
    [/^Too small: expected array to have >=?(\d+) items?$/, 'needs at least $1.'],
    [/^Too big: expected array to have <=?(\d+) items?$/, 'allows at most $1.'],
    [/^Too small: expected number to be >=?(\d+)$/, 'must be at least $1.'],
    [/^Too big: expected number to be <=?(\d+)$/, 'must be at most $1.'],
    [/^Invalid input: expected \w+, received undefined$/, 'is required.'],
    [/^Invalid email address$/, 'is not a valid email address.'],
  ];
  for (const [pattern, replacement] of rules) {
    if (pattern.test(message)) return message.replace(pattern, replacement);
  }
  return `- ${message}`;
}
